import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Play,
  Gamepad2,
  X,
  Maximize2,
  Minimize2,
  ChevronLeft,
  ChevronRight,
  ImageIcon,
} from "lucide-react";
import { apiRequest } from "../utils/api";
import { GAME_SCREENSHOTS } from "../data/gameScreenshots";
import { GAME_DESCRIPTIONS } from "../data/gameDescriptions";

// Thumbnail map (same as Home/Library)
import trapAndGooImg from "../assets/trap-and-goo.webp";
import forestRushImg from "../assets/forest-rush.webp";
import draugutsImg from "../assets/drauguts.jpg";
import frontlineImg from "../assets/frontline.webp";
import cyrusBeanJumpImg from "../assets/cyrus-bean-jump.webp";
import dandoRushImg from "../assets/dando-rush.webp";
import draughtsImg from "../assets/draughts.webp";
import gunWarsImg from "../assets/gun-wars.webp";
import omiImg from "../assets/omi.webp";
import poolLeagueImg from "../assets/pool-league.webp";
import carromLeagueImg from "../assets/carrom-league.webp";

const THUMBNAIL_MAP = {
  "trap-and-goo.jpg": trapAndGooImg,
  "trap-and-goo.webp": trapAndGooImg,
  "forest-rush.jpg": forestRushImg,
  "forest-rush.webp": forestRushImg,
  "drauguts.jpg": draugutsImg,
  "drauguts.webp": draugutsImg,
  "draughts.jpg": draughtsImg,
  "draughts.webp": draughtsImg,
  "frontline.jpg": frontlineImg,
  "frontline.webp": frontlineImg,
  "cyrus-bean-jump.webp": cyrusBeanJumpImg,
  "dando-rush.webp": dandoRushImg,
  "gun-wars.webp": gunWarsImg,
  "omi.webp": omiImg,
  "pool-league.webp": poolLeagueImg,
  "carrom-league.webp": carromLeagueImg,
  "Trap and Goo": trapAndGooImg,
  "Forest Rush": forestRushImg,
  "Draughts": draughtsImg,
  "Frontline": frontlineImg,
  "Cyrus Bean Jump": cyrusBeanJumpImg,
  "Dando Rush": dandoRushImg,
  "Gun Wars": gunWarsImg,
  "Omi": omiImg,
  "Pool League": poolLeagueImg,
  "Carrom League": carromLeagueImg,
};

const GameDetail = () => {
  const { gameId } = useParams();
  const navigate = useNavigate();
  const [game, setGame] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeGame, setActiveGame] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const gameContainerRef = useRef(null);

  // Fetch game data from API
  useEffect(() => {
    const fetchGame = async () => {
      try {
        setLoading(true);
        const res = await apiRequest("/Games/library");
        if (res.ok) {
          const data = await res.json();
          const found = data.find((g) => String(g.id) === String(gameId));
          if (found) {
            setGame({
              id: found.id,
              title: found.title,
              category: found.genre,
              buildPath: found.launchUrl || "",
              multiplayerType: found.multiplayerType || "",
              image:
                (found.thumbnailUrl && THUMBNAIL_MAP[found.thumbnailUrl]) ||
                THUMBNAIL_MAP[found.title] ||
                null,
            });
          }
        }
      } catch (err) {
        // silently fail
      } finally {
        setLoading(false);
      }
    };
    fetchGame();
  }, [gameId]);

  // Manage body scroll lock when gameplay is active
  useEffect(() => {
    if (activeGame) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [activeGame]);

  // Fullscreen change listener
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () =>
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      const elem = gameContainerRef.current || document.documentElement;
      if (elem.requestFullscreen) {
        elem.requestFullscreen().catch(() => {});
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen().catch(() => {});
      }
    }
  };

  const handleCloseGame = () => {
    if (document.fullscreenElement && document.exitFullscreen) {
      document.exitFullscreen().catch(() => {});
    }
    setActiveGame(null);
  };

  const handlePlayGame = () => {
    const token = localStorage.getItem("accessToken");
    if (!token) {
      navigate("/login");
    } else if (!game.buildPath) {
      return;
    } else {
      setActiveGame(game);
    }
  };

  // --- FULL SCREEN GAMEPLAY VIEW (same as Home/Library) ---
  if (activeGame) {
    return createPortal(
      <div
        ref={gameContainerRef}
        className="fixed inset-0 w-screen h-screen bg-black text-white flex flex-col z-[99999] overflow-hidden"
        style={{
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          margin: 0,
          padding: 0,
        }}
      >
        {/* Controls Overlay: Fullscreen Toggle & Close Button */}
        <div className="absolute top-4 right-4 z-[100000] flex items-center gap-3">
          <button
            onClick={toggleFullscreen}
            className="flex items-center gap-2 px-4 py-2 bg-black/85 hover:bg-black border border-white/20 hover:border-[#FF7A00] text-white font-heading font-black uppercase tracking-wider text-xs rounded-xl shadow-lg transition-all duration-300 hover:shadow-[0_0_15px_rgba(255,122,0,0.5)] cursor-pointer group"
            title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
          >
            {isFullscreen ? (
              <>
                <Minimize2
                  size={16}
                  className="text-gray-400 group-hover:text-[#FF7A00] transition-colors"
                />
                <span className="hidden sm:inline">Exit Fullscreen</span>
              </>
            ) : (
              <>
                <Maximize2
                  size={16}
                  className="text-gray-400 group-hover:text-[#FF7A00] transition-colors"
                />
                <span className="hidden sm:inline">Fullscreen</span>
              </>
            )}
          </button>

          <button
            onClick={handleCloseGame}
            className="flex items-center gap-2 px-4 py-2 bg-black/85 hover:bg-black border border-white/20 hover:border-[#FF7A00] text-white font-heading font-black uppercase tracking-wider text-xs rounded-xl shadow-lg transition-all duration-300 hover:shadow-[0_0_15px_rgba(255,122,0,0.5)] cursor-pointer group"
          >
            <X
              size={16}
              className="text-gray-400 group-hover:text-[#FF7A00] transition-colors"
            />
            <span>Close Game</span>
          </button>
        </div>

        {/* Central Full Screen WebGL Game Canvas */}
        <div className="w-full h-full relative flex items-center justify-center bg-black">
          <iframe
            src={
              activeGame.buildPath.includes("?")
                ? `${activeGame.buildPath}&token=${encodeURIComponent(
                    localStorage.getItem("accessToken") || ""
                  )}`
                : `${activeGame.buildPath}?token=${encodeURIComponent(
                    localStorage.getItem("accessToken") || ""
                  )}`
            }
            className="w-full h-full border-none shadow-2xl"
            title={activeGame.title}
            allow="autoplay; fullscreen; keyboard; gamepad"
          />
        </div>
      </div>,
      document.body
    );
  }

  // --- LOADING STATE ---
  if (loading) {
    return (
      <div className="animate-in fade-in duration-500 pb-12">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-xl bg-[#1E1E24] animate-pulse" />
          <div className="h-6 w-48 bg-[#1E1E24] rounded-lg animate-pulse" />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-8">
          <div>
            <div className="w-full aspect-video rounded-2xl bg-[#1E1E24] animate-pulse mb-6" />
            <div className="grid grid-cols-3 gap-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="aspect-video rounded-xl bg-[#1E1E24] animate-pulse" />
              ))}
            </div>
          </div>
          <div className="space-y-4">
            <div className="h-4 w-3/4 bg-[#1E1E24] rounded animate-pulse" />
            <div className="h-4 w-full bg-[#1E1E24] rounded animate-pulse" />
            <div className="h-4 w-2/3 bg-[#1E1E24] rounded animate-pulse" />
            <div className="h-12 w-full bg-[#1E1E24] rounded-xl animate-pulse mt-8" />
          </div>
        </div>
      </div>
    );
  }

  // --- GAME NOT FOUND ---
  if (!game) {
    return (
      <div className="animate-in fade-in duration-500 pb-12 text-center py-20">
        <Gamepad2 size={64} className="mx-auto mb-4 text-gray-600" />
        <h2 className="font-heading font-black text-2xl text-white mb-2">
          Game Not Found
        </h2>
        <p className="text-gray-400 text-sm mb-6">
          The game you're looking for doesn't exist or has been removed.
        </p>
        <button
          onClick={() => navigate("/")}
          className="game-btn px-8 py-3 rounded-xl text-sm"
        >
          Back to Home
        </button>
      </div>
    );
  }

  // Get screenshots & description for this game
  const screenshots = GAME_SCREENSHOTS[game.title] || [];
  const description =
    GAME_DESCRIPTIONS[game.title] ||
    "Get ready for an incredible gaming experience! Jump in and play now.";

  return (
    <div className="animate-in fade-in duration-500 pb-12">
      {/* Back Button */}
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors mb-6 group"
      >
        <ArrowLeft
          size={18}
          className="group-hover:-translate-x-1 transition-transform"
        />
        <span className="text-sm font-semibold uppercase tracking-wider">
          Back
        </span>
      </button>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-8 lg:gap-10">
        {/* LEFT: Hero Image + Screenshots */}
        <div>
          {/* Hero / Main Game Image */}
          <div className="relative w-full aspect-video rounded-2xl overflow-hidden border border-[#2B2B38] shadow-[0_15px_35px_rgba(0,0,0,0.6)] group mb-6">
            {game.image ? (
              <img
                src={game.image}
                alt={game.title}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
              />
            ) : (
              <div className="w-full h-full bg-gradient-to-br from-[#1E1E28] to-[#121217] flex items-center justify-center">
                <Gamepad2 size={64} className="text-gray-600" />
              </div>
            )}
            {/* Gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent pointer-events-none" />
            {/* Game title overlay */}
            <div className="absolute bottom-4 left-5 z-10">
              <h1 className="font-heading font-black italic uppercase text-3xl sm:text-4xl md:text-5xl text-white drop-shadow-[0_4px_12px_rgba(0,0,0,0.9)] tracking-tight">
                {game.title}
              </h1>
              <div className="flex items-center gap-3 mt-2">
                <span className="bg-[#FF5E0E]/20 border border-[#FF5E0E]/40 text-[#FF8A00] px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest">
                  {game.category || "Action"}
                </span>
                {game.multiplayerType && (
                  <span className="bg-[#FFB800]/10 border border-[#FFB800]/30 text-[#FFB800] px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest">
                    {game.multiplayerType}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Screenshots Grid */}
          {screenshots.length > 0 && (
            <div>
              <h3 className="font-heading font-black text-sm uppercase tracking-widest text-gray-400 mb-3 flex items-center gap-2">
                <ImageIcon size={14} className="text-[#FF7A00]" />
                Screenshots
              </h3>
              <div className="grid grid-cols-3 gap-3">
                {screenshots.map((screenshot, index) => (
                  <button
                    key={index}
                    onClick={() => setLightboxIndex(index)}
                    className="relative aspect-video rounded-xl overflow-hidden border border-[#2B2B38] hover:border-[#FF7A00]/60 transition-all duration-300 group/ss cursor-pointer shadow-lg hover:shadow-[0_8px_20px_rgba(255,100,0,0.2)] hover:-translate-y-0.5"
                  >
                    <img
                      src={screenshot}
                      alt={`${game.title} Screenshot ${index + 1}`}
                      className="w-full h-full object-cover group-hover/ss:scale-110 transition-transform duration-500"
                    />
                    {/* Hover overlay */}
                    <div className="absolute inset-0 bg-black/0 group-hover/ss:bg-black/30 transition-colors duration-300 flex items-center justify-center">
                      <Maximize2
                        size={20}
                        className="text-white opacity-0 group-hover/ss:opacity-100 transition-opacity duration-300 drop-shadow-lg"
                      />
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* RIGHT: Description & Play Button */}
        <div className="flex flex-col">
          {/* Info Card */}
          <div className="glass-panel rounded-2xl p-6 space-y-5 flex-1">
            <div>
              <h2 className="font-heading font-black italic uppercase text-xl tracking-tight text-white mb-1">
                {game.title}
              </h2>
              <div className="w-12 h-1 bg-gradient-to-r from-[#FF5E0E] to-[#FFB800] rounded-full" />
            </div>

            <div>
              <h4 className="text-[10px] font-black uppercase tracking-[0.2em] text-gray-500 mb-2">
                About This Game
              </h4>
              <p className="text-gray-300 text-sm leading-relaxed">
                {description}
              </p>
            </div>

            <div className="flex flex-wrap gap-3">
              {game.category && (
                <div className="bg-[#16161F] border border-[#2B2B38] rounded-xl px-4 py-2.5">
                  <span className="text-[9px] font-black uppercase tracking-[0.2em] text-gray-500 block">
                    Genre
                  </span>
                  <span className="text-white text-sm font-bold">
                    {game.category}
                  </span>
                </div>
              )}
              {game.multiplayerType && (
                <div className="bg-[#16161F] border border-[#2B2B38] rounded-xl px-4 py-2.5">
                  <span className="text-[9px] font-black uppercase tracking-[0.2em] text-gray-500 block">
                    Mode
                  </span>
                  <span className="text-white text-sm font-bold">
                    {game.multiplayerType}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Play Button */}
          <button
            onClick={handlePlayGame}
            disabled={!game.buildPath}
            className={`mt-5 w-full py-4 rounded-2xl font-heading font-black uppercase tracking-widest text-base flex items-center justify-center gap-3 transition-all duration-300 active:scale-95 shadow-xl ${
              game.buildPath
                ? "bg-gradient-to-r from-[#FF5E0E] to-[#FF8A00] hover:from-[#FF6E1E] hover:to-[#FF9A10] text-white shadow-orange-500/30 hover:shadow-[0_0_30px_rgba(255,94,14,0.6)] cursor-pointer"
                : "bg-[#2B2B38] text-gray-500 cursor-not-allowed"
            }`}
          >
            <Play
              size={20}
              className={game.buildPath ? "fill-white" : "fill-gray-500"}
            />
            {game.buildPath ? "Play Now" : "Coming Soon"}
          </button>
        </div>
      </div>

      {/* Screenshot Lightbox Modal */}
      {lightboxIndex !== null && screenshots.length > 0 && (
        <div
          className="fixed inset-0 z-[99998] bg-black/90 backdrop-blur-md flex items-center justify-center p-4"
          onClick={() => setLightboxIndex(null)}
        >
          {/* Close button */}
          <button
            onClick={() => setLightboxIndex(null)}
            className="absolute top-5 right-5 z-10 flex items-center gap-2 px-4 py-2 bg-black/70 hover:bg-black border border-white/20 hover:border-[#FF7A00] text-white font-black uppercase tracking-wider text-xs rounded-xl transition-all duration-300 cursor-pointer"
          >
            <X size={16} />
            Close
          </button>

          {/* Nav arrows */}
          {screenshots.length > 1 && (
            <>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setLightboxIndex(
                    (lightboxIndex - 1 + screenshots.length) %
                      screenshots.length
                  );
                }}
                className="absolute left-4 z-10 p-3 bg-black/60 hover:bg-black/80 border border-white/20 hover:border-[#FF7A00] rounded-full text-white transition-all duration-300 cursor-pointer"
              >
                <ChevronLeft size={24} />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setLightboxIndex((lightboxIndex + 1) % screenshots.length);
                }}
                className="absolute right-4 z-10 p-3 bg-black/60 hover:bg-black/80 border border-white/20 hover:border-[#FF7A00] rounded-full text-white transition-all duration-300 cursor-pointer"
              >
                <ChevronRight size={24} />
              </button>
            </>
          )}

          {/* Image */}
          <img
            src={screenshots[lightboxIndex]}
            alt={`${game.title} Screenshot ${lightboxIndex + 1}`}
            className="max-w-full max-h-[85vh] rounded-2xl shadow-2xl object-contain border border-[#2B2B38]"
            onClick={(e) => e.stopPropagation()}
          />

          {/* Dot indicators */}
          <div className="absolute bottom-6 flex gap-2">
            {screenshots.map((_, idx) => (
              <button
                key={idx}
                onClick={(e) => {
                  e.stopPropagation();
                  setLightboxIndex(idx);
                }}
                className={`h-2 rounded-full transition-all duration-300 ${
                  idx === lightboxIndex
                    ? "w-6 bg-[#FF7A00] shadow-[0_0_10px_rgba(255,122,0,0.8)]"
                    : "w-2 bg-white/30 hover:bg-white/60"
                }`}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default GameDetail;
